<script setup>
import { Head, router } from "@inertiajs/vue3";
import { themes } from "../../../_Themes/themes";
import { onMounted, reactive, ref } from "vue";

import {
    searchCustomer,
    createCustomer,
    searchInvoice,
    createInvoice,
    createInvoiceItem,
    finalizeInvoice,
    sendInvoice,
    getInvoiceEcommerce,
    setVoidInvoice,
    getInvoice,
    createSession,
} from "../../../_Services/checkoutService.js";
import {
    initStripe,
    createPaymentElement,
    mountPaymentElement,
    confirmPaymentElement,
    mountCheckout,
} from "../../../_Services/stripeElements.js";
import { retrievePaymentIntentStripe } from "../../../_Services/customerService.js";

import CheckoutLayout from "../../Layouts/CheckoutLayout.vue";
import Loader from "./Components/Loader.vue";
import LoaderPayment from "./Components/LoaderPayment.vue";
import ItemCard from "./Components/ItemCard.vue";
import { data } from "autoprefixer";

import { BuildingOfficeIcon, CreditCardIcon } from "@heroicons/vue/20/solid";

const themeSelected = themes("pactoglobal");

const props = defineProps({
    invoiceRequest: {
        type: Object,
        required: true,
    },
});

const stripeAccount = import.meta.env.VITE_PACTOGLOBAL_ACCOUNT_ID;
const stripeProductId = import.meta.env.VITE_PACTOGLOBAL_PRODUCT_ID;

/**
 * Loading: While we get information from backend (swoogo)
 * Mounting: While we create and mount stripe elements
 * Proccessing: While we send payment information with stripe to finalice invoice
 */
const loading = ref(false);
const loadingError = ref(null);
const mountingElement = ref(false);
const mountingElementError = ref(null);
const proccesing = ref(false);
const proccesingError = ref(null);
const stripeUser = ref(null);
const creatingInvoice = ref(false);
const stripeElementSelected = ref(false);

const stripeInvoice = reactive({
    openInvoice: null,
    data: null,
    totalAmount: null,
    remainingAmount: null,
});

const swoogoInvoice = ref(null)
/**
 * Get invoice from swoogo service
 */
const getSwoogoInvoice = async () => {
    loading.value = true;

    try {
        const params = {}
        const response = await getInvoiceEcommerce(stripeAccount, props.invoiceRequest, params)
        swoogoInvoice.value = response.data

        if (swoogoInvoice.value.payment_status.value === "Paid"){
            throw new Error('Esta factura ya ha sido pagada')
        }

        if (swoogoInvoice.value.group_balance_due < 10){
            throw new Error('No se permiten hacer pagos por montos menores a $10.00')
        }

        searchStripeCustomer();
    } catch (error) {
        loading.value = false;
        loadingError.value =
            error.response?.data?.message ||
            error.message ||
            "Ocurrió un error desconocido al obtener la información de pago.";

    }
};

/**
 * Search an existing customer on stripe account searching by metadata field to get info or created it
 */
const searchStripeCustomer = async () => {

    try {
        const userEmail = swoogoInvoice.value.email;
        const query = `metadata[\'pai_swoogo_email\']:\'${userEmail}\'`;
        const response = await searchCustomer(stripeAccount, query);

        if (response.data.data.length > 0) {
            stripeUser.value = response.data.data[0];
        } else {
            await createStripeCustomer();
        }

        /**
         * Save info in session storage to read in thankyou page
         */
        sessionStorage.setItem("customerId", stripeUser.value.id)
        sessionStorage.setItem("customerStripeName", stripeUser.value.name)
        
        findCurrentInvoice();
    } catch (error) {
        loading.value = false;
        loadingError.value =
            error.response?.data?.message ||
            error.message ||
            "Ocurrió un error desconocido al buscar al cliente en Stripe.";
    }
};

/**
 * Create new stripe customer with swoogo invoice data
 */
const createStripeCustomer = async () => {
    const data = {
        name: `${swoogoInvoice.value.first_name} ${swoogoInvoice.value.last_name}`,
        email: swoogoInvoice.value.email,
        metadata: {
            pai_swoogo_email: swoogoInvoice.value.email,
        },
    };
    try {
        const response = await createCustomer(stripeAccount, data);
        stripeUser.value = response.data;
    } catch (error) {
        loading.value = false;
        loadingError.value =
            error.response?.data?.message ||
            error.message ||
            "Ocurrió un error desconocido al crear el cliente en Stripe.";
    }
};

/**
 * Search for an invoice related with the same swoogo invoice by metadata field
 */
const findCurrentInvoice = async () => {
    const query = `metadata[\'swoogo_secure_id\']:\'${swoogoInvoice.value.secure_id}\'`;
    const invoicesFinded = {
        draft: [],
        open: [],
        paid: [],
    }
    try {
        await new Promise(resolve => setTimeout(resolve, 3000));
        const expandOptions = ['data.payment_intent.latest_charge']
        const response = await searchInvoice(stripeAccount, query, expandOptions);

        if (response?.data?.data?.length > 0) {
            for (const invoice of response.data.data) {
                if (invoice.status === 'draft') {
                    invoicesFinded.draft.push(invoice)
                } else if (invoice.status === 'open') {
                    invoicesFinded.open.push(invoice)
                } else if (invoice.status === 'paid') {
                    invoicesFinded.paid.push(invoice)
                }
            }
        }

        if (invoicesFinded.draft.length > 0) {
            // If there are a draft invoice, we finalize it
            const isCorrect = invoicesFinded.draft[0].amount_due === (swoogoInvoice.value.group_balance_due*100) ? true : false;

            if (!isCorrect) {
                // If the amount is not correct, we void the draft invoice and create a new one
                await finalizeCurrentInvoice(invoicesFinded.draft[0], false, false)
                await setToVoidInvoice(invoicesFinded.draft[0])
                await setNewInvoice()
                stripeInvoice.totalAmount = swoogoInvoice.value.group_balance_due*100
                return;
            }

            if (invoicesFinded.draft[0].amount_remaining !== invoicesFinded.draft[0].amount_due) {
                stripeInvoice.remainingAmount = invoicesFinded.draft[0].amount_remaining
            }

            finalizeCurrentInvoice(invoicesFinded.draft[0], false, true)
        } else if (invoicesFinded.open.length > 0) {
            // If there are open invoices, we use the first one
            const isCorrect = invoicesFinded.open[0].amount_due === (swoogoInvoice.value.group_balance_due*100) ? true : false;

            if (!isCorrect) {
                // If the amount is not correct, we set to void the invoice and create a new one
                await setToVoidInvoice(invoicesFinded.open[0])
                await setNewInvoice()
                stripeInvoice.totalAmount = swoogoInvoice.value.group_balance_due*100
                return;
            }

            if (invoicesFinded.open[0].amount_remaining !== invoicesFinded.open[0].amount_due) {
                stripeInvoice.remainingAmount = invoicesFinded.open[0].amount_remaining
            }

            stripeInvoice.openInvoice = invoicesFinded.open[0]
            stripeInvoice.openInvoice.payment_intent = invoicesFinded.open[0].payment_intent.id
        } else if (invoicesFinded.paid.length > 0) {
            // If there are paid invoices, we show an error
            if (invoicesFinded.paid[0]?.payment_intent?.latest_charge?.paid && invoicesFinded.paid[0]?.payment_intent?.latest_charge?.refunded) {
                // If the paid invoice was refunded, we create a new one
                await setNewInvoice()
                stripeInvoice.totalAmount = swoogoInvoice.value.group_balance_due*100
                return;
            }
            throw new Error('Esta factura ya ha sido pagada en su totalidad.');
            return;
        } else {
            // If there are no invoices, we create a new one
            await setNewInvoice()
        }
        stripeInvoice.totalAmount = swoogoInvoice.value.group_balance_due
    } catch (error) {
        loading.value = false;
        loadingError.value =
            error.response?.data?.message ||
            error.message ||
            "Ocurrió un error desconocido al crear el cliente en Stripe.";
    } finally {
        loading.value = false;
    }
};

const setToVoidInvoice = async (invoiceObject) => {
    try {
        const response = await setVoidInvoice(stripeAccount, invoiceObject.id)
    } catch (e) {
        loading.value = false;
        loadingError.value =
            e.response?.data?.message ||
            e.message ||
            "Ocurrió un error desconocido al cancelar la factura en Stripe.";
    }
}

/**
 * We create a new stripe invoice adding metadata and description required
 */
const setNewInvoice = async (regeneratingInvoice = false) => {
    const params = {
        customer: stripeUser.value.id,
        description: `Cuota de recuperación-${swoogoInvoice.value.event.name}`,
        metadata: {
            swoogo_secure_id: swoogoInvoice.value.secure_id,
            event_name: swoogoInvoice.value.event.name,
            pai_pacto_global_id: swoogoInvoice.value.id,
        },
        collection_method: 'send_invoice',
        days_until_due: 5,
        payment_settings: {
            payment_method_options: {
                card: {
                    request_three_d_secure: 'automatic',
                },
                customer_balance: {
                    bank_transfer: {
                        type: 'mx_bank_transfer'
                    },
                    funding_type: 'bank_transfer',
                },
            },
            payment_method_types: ['card', 'customer_balance'],
        },
    };

    try {
        if (creatingInvoice.value) {
            return;
        }
        creatingInvoice.value = true;
        const response = await createInvoice(stripeAccount, params);
        await setInvoiceItems(response.data, regeneratingInvoice)
    } catch (e) {
        mountingElement.value = false;
        mountingElementError.value =
            e.response?.data?.message ||
            e.message ||
            "Ocurrió un error desconocido al crear la factura en Stripe.";
    }
};

/**
 * When we create tne invoice, we neec to add items to invoice with status draft, this function add items to a previus invoice created
 */
const setInvoiceItems = async (invoiceCreated, regeneratingInvoice = false) => {
    const dataInvoiceItem = [
        {
            customer: stripeUser.value.id,
            description: `Cuota de recuperación-${swoogoInvoice.value.event.name}`,
            invoice: invoiceCreated.id,
            quantity: 1,
            price_data: {
                currency: "MXN",
                product: stripeProductId,
                unit_amount_decimal:
                    swoogoInvoice.value.group_balance_due * 100,
            },
        },
    ];
    try {
        const response = await createInvoiceItem(
            stripeAccount,
            dataInvoiceItem
        );
        await finalizeCurrentInvoice(invoiceCreated, regeneratingInvoice, true);
    } catch (e) {
        mountingElement.value = false;
        mountingElementError.value =
            e.response?.data?.message ||
            e.message ||
            "Ocurrió un error desconocido al crear agregar el producto a la factura de Stripe.";
    }
};

/**
 * We close the invoice to change status and be able to pay by customer
 */
const finalizeCurrentInvoice = async (invoiceCreated, regeneratingInvoice = false, fromSetInvoice = false) => {
    try {
        const response = await finalizeInvoice(stripeAccount, invoiceCreated.id);
        stripeInvoice.openInvoice = response.data;

        /**s
         * RegeneratinInvoice part
         * In this point, we moved to send invoice function if the current function detect regeneratingInvoice = true
         */
        if (regeneratingInvoice) {
            await sendEmailInvoice()
        } else if (fromSetInvoice) {
            // When we finalize creating the invoice, update loading value to show customer buttons to pay via card or spei
            ui.value.loading = false
            ui.value.mountingPaymentElement = false
        }
    } catch (e) {
        mountingElement.value = false;
        mountingElementError.value =
            e.response?.data?.message ||
            e.message ||
            "Ocurrió un error desconocido al cerrar la factura de Stripe.";
    }
};

//SIGUE ACTUALIZAR FORMA AL DAR CLICK EN BOTON DE PAGAR CON TARJETA O SPEI
//  (YA DEBE ESTAR CREADA LA FACTURA EN ESTE PUNTO NO SE DEBE ESPERAR CONFIRMACIÓN DEL CLIENTE)
const showInvoiceToPay = async () => {
    mountingElement.value = true;
    initStripe(stripeAccount);
    const options = {
        layout: "accordion",
    };
    try {
        const responsePaymentIntent = await retrievePaymentIntentStripe(
            stripeAccount,
            stripeUser.value.id,
            stripeInvoice.openInvoice.payment_intent
        );
        createPaymentElement(responsePaymentIntent.data.client_secret, options);
        mountPaymentElement();
        stripeElementSelected.value = true;
        mountingElement.value = false;

        const paymentFormEl = document.getElementById('payment-form');
        if (paymentFormEl) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            paymentFormEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    } catch (error) {
        mountingElement.value = false;
        mountingElementError.value =
            error.response?.data?.message ||
            error.message ||
            "Ocurrió un error desconocido al montar Stripe Elements en Checkout";
    }
};

/**
 * We send an email with instructions to pay if they choose "pay via SPEI"
 */
const sendEmailInvoice = async () => {
    mountingElement.value = true;
    try {
        if (stripeInvoice.openInvoice.collection_method !== "send_invoice") {
            await setToVoidInvoice(stripeInvoice.openInvoice);
            await setNewInvoice(true);
        } else {
            const response = await sendInvoice(
                stripeAccount,
                stripeInvoice.openInvoice.id
            );
            router.visit(route('pactoglobal.thankyou'), { replace: true });
        }
    } catch (error) {
        mountingElement.value = false;
        mountingElementError.value =
            error.response?.data?.message ||
            error.message ||
            "Ocurrió un error desconocido al crear la factura en Stripe.";
    }
};

/**
 * Function to procces payment throught stripe
 */
const handlePaymentSubmit = async () => {
    proccesing.value = true;
    proccesingError.value = null;
    try {
        const successUrl = route('pactoglobal.thankyou');
        const response = await confirmPaymentElement(successUrl);

        if (
            response?.error?.type === "card_error" ||
            response?.error?.type === "validation_error"
        ) {
            proccesing.value = false;
            throw new Error(response.error.message);
        } else {
            throw new Error("Error desconocido al procesar tu pago");
        }
    } catch (error) {
        proccesing.value = false;
        proccesingError.value =
            error.response?.data?.message ||
            error.message ||
            "Error desconocido al procesar el pago.";
    }
};

onMounted(() => {
    getSwoogoInvoice();
});
</script>
<template>

    <Head title="Checkout" />
    <CheckoutLayout :themeSelected="themeSelected">
        <div class="min-h-screen mx-auto max-w-7xl bg-white p-10">
            <div class="w-full grid grid-cols-1 md:grid-cols-2 gap-16 md:border-t-2 md:border-[#5B7995]">
                <div class="place-items-center">
                    <Loader v-if="loading" />
                    <div v-else-if="loadingError" class="my-16 md:my-28">
                        <p class="text-xl text-center text-pactoglobal-blue-text font-bold uppercase">
                            {{ loadingError }}
                        </p>
                    </div>
                    <div v-else-if="!loading && !loadingError"
                        class="mx-auto w-full sm:w-2/3 lg:w-3/4 rounded-xl md:mt-5 lg:px-8 font-Poppins">
                        <ItemCard v-if="swoogoInvoice" :swoogoInvoice="swoogoInvoice" />
                        <div id="payment-options" class="mt-5">
                            <p class="text-sm md:text-base font-bold">
                                Método de pago
                            </p>
                            <div class="flex flex-col items-start gap-2 mt-3">
                                <button :disabled="loading ||
                                    proccesing ||
                                    mountingElement" @click.prevent="showInvoiceToPay"
                                    :class="[stripeElementSelected ? 'bg-pactoglobal-blue-dark/10' : '', 'w-full py-3 px-4 text-left text-gray-700 font-semibold flex items-center gap-3 ring-1 ring-gray-300 rounded-xl hover:bg-pactoglobal-blue-dark/5']">
                                    <CreditCardIcon class="ml-3 mr-1 h-5 w-5 text-gray-500" />
                                    <span>Tarjeta</span>
                                </button>
                                <button :disabled="loading ||
                                    proccesing ||
                                    mountingElement" @click.prevent="sendEmailInvoice"
                                    :class="['w-full py-3 px-4 text-left text-gray-700 font-semibold flex items-center gap-3 ring-1 ring-gray-300 rounded-xl hover:bg-pactoglobal-blue-dark/5']">
                                    <BuildingOfficeIcon class="ml-3 mr-1 h-5 w-5 text-gray-500" />
                                    <span>SPEI</span>
                                </button>
                            </div>
                        </div>
                        <LoaderPayment v-if="mountingElement" />
                        <form id="payment-form" @submit.prevent="handlePaymentSubmit" :class="[`${!mountingElement ? 'py-6' : '' } w-full h-auto mt-5 space-y-3`]">
                            <div id="payment-element">
                                <!--Stripe.js injects the Payment Element-->
                            </div>
                            <button v-if="stripeElementSelected" :disabled="loading || proccesing || mountingElement"
                                class="px-4 py-3 flex items-center justify-center bg-pactoglobal-blue-text rounded-xl w-full text-lg font-bold uppercase text-pactoglobal-blue-secondary disabled:cursor-not-allowed">
                                <span v-if="!proccesing" id="button-text">Pagar</span>
                                <svg v-else class="animate-spin h-5 w-5 text-white mr-2"
                                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                        stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                    </path>
                                </svg>
                            </button>
                            <div id="payment-message">
                                <p class="text-red-500 text-sm text-center mt-2">
                                    {{ proccesingError }}
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
                <div class="md:justify-self-end">
                    <img src="/checkouts/pactoglobal/img/pactoglobal-grid-images.png" alt="Logo"
                        class="object-contain md:object-right h-auto w-full -mt-[3px] max-w-[500px]" />
                </div>
            </div>
        </div>
    </CheckoutLayout>
</template>
